import { createSlice, createSelector, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '@store/store';
import { getCurrentLocation } from '@utils/location.utils';

// Types and initial state of user location
//-------------------------
export type Coordinates = {
  latitude: number;
  longitude: number;
};

type Permission = 'granted' | 'denied' | 'prompt' | null;

export interface LocationState {
  location: Coordinates | null;
  permission: Permission;
}

const initialState: LocationState = {
  location: null,
  permission: null,
};

// REDUCERS
export const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    setLocation: (state, action: PayloadAction<Coordinates | null>) => {
      state.location = action.payload;
    },
    setPermission: (state, action: PayloadAction<Permission>) => {
      state.permission = action.payload;
    },
  },
});

export const fetchUserLocation = createAsyncThunk(
  'location/fetchUserLocation',
  async (_, { dispatch }) => {
    try {
      const location = await getCurrentLocation();
      dispatch(setLocation(location));
    } catch (error) {
      console.error('Error fetching location:', error);
      dispatch(setLocation(null));
    }
  }
);

export const getBrowserLocation = createAsyncThunk(
  'location/getBrowserLocation',
  async (_, { dispatch }) => {
    if (navigator.permissions) {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      dispatch(setPermission(permission.state));
      if (permission.state === 'granted') {
        dispatch(fetchUserLocation());
      }
      permission.onchange = () => {
        dispatch(setPermission(permission.state));
      };
    }
  }
);

// ACTIONS
export const { setLocation, setPermission } = locationSlice.actions;

// Selectors
export const getUserLocation = createSelector(
  (state: RootState) => state.location,
  (location) => location
);

export default locationSlice.reducer;
